import { motion } from "framer-motion";
import { Home, Search, ArrowLeft, MessageCircle, Zap, Brain, MapPin, RefreshCw, ExternalLink } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigateWithScroll } from "../hooks/useNavigateWithScroll";

export const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFloating, setIsFloating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setIsFloating(prev => !prev);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const popularPages = [
    { 
      name: t('404.popularPages.extension'), 
      path: "/extension", 
      icon: Brain,
      description: t('404.popularPages.extensionDesc')
    },
    { 
      name: t('404.popularPages.features'), 
      path: "/features", 
      icon: Zap,
      description: t('404.popularPages.featuresDesc')
    },
    { 
      name: t('404.popularPages.pricing'), 
      path: "/pricing", 
      icon: MessageCircle,
      description: t('404.popularPages.pricingDesc')
    }
  ];

  const handleSearch = (e: any) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Rediriger vers une page de recherche ou effectuer une recherche
      console.log("Recherche:", searchQuery);
      // window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const goBack = () => {
    window.history.back();
  };

  const goHome = () => {
    navigate("/");
  };
  const navigate = useNavigateWithScroll()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 flex flex-col">
      
      {/* Particules flottantes en arrière-plan */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-blue-200 dark:bg-blue-800/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* Animation 404 principale */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <div className="relative inline-block">
              {/* Effet de brillance */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                animate={{ x: [-100, 300] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              />
              
              <h1 className="text-8xl lg:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 relative">
                404
              </h1>
            </div>
            
            {/* Robot/IA flottant */}
            <motion.div
              animate={{ 
                y: isFloating ? -10 : 10,
                rotate: isFloating ? -5 : 5 
              }}
              transition={{ duration: 3, ease: "easeInOut" }}
              className="inline-block mt-4"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Brain className="w-10 h-10 text-white" />
              </div>
            </motion.div>
          </motion.div>

          {/* Titre et description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('404.title')}
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-2">
              {t('404.description')}
            </p>
            <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{t('404.location')}</span>
            </div>
          </motion.div>

          {/* Barre de recherche */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-12"
          >
            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                //   placeholder={t('404.searchPlaceholder')}
                  className="w-full px-6 py-4 pr-12 text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                />
                <button
                  onClick={handleSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Boutons d'action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-16"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goHome}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              <Home className="w-5 h-5" />
              <span>{t('404.goHome')}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={goBack}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('404.goBack')}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="inline-flex items-center space-x-2 px-6 py-4 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              <span>{t('404.refresh')}</span>
            </motion.button>
          </motion.div>

          {/* Pages populaires */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
              {t('404.popularPagesTitle')}
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {popularPages.map((page, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  onClick={()=>{navigate(page.path)}}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="block p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 transition-all group"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <page.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {page.name}
                      </h4>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    {page.description}
                  </p>
                  <div className="flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium">
                    <span>{t('404.visit')}</span>
                    <ExternalLink className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Message d'aide */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-16 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800"
          >
            <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400 mb-2">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">{t('404.help.title')}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('404.help.description')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Footer minimal */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="relative py-8 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        <div className="flex items-center justify-center space-x-4">
          <span>© 2024 3wChat</span>
          <span>•</span>
          <a href="/support" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
            {t('404.footer.support')}
          </a>
          <span>•</span>
          <a href="/status" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
            {t('404.footer.status')}
          </a>
        </div>
      </motion.footer>
    </div>
  );
};